import { GMXLocationSingletonApi, LOCATION_READY_EVENT } from '../models/location.model'
import { AddressGeoDetail } from './postalCode'

/**
 * Reach the shared location singleton.
 *
 * Consumers always go through `window.gmxLocationSingleton` rather than importing the
 * implementation: every component outside the `js/core` bundle that imported it would ship a
 * second copy of the class and instantiate a rival instance, leaving two objects reading and
 * writing the same cookie with different in-flight state.
 *
 * Component bundles can load ahead of core, so a caller that needs the singleton during
 * startup waits for it to announce itself.  Anything happening in response to a visitor
 * interaction can read `window.gmxLocationSingleton` directly — core has long since loaded.
 */
export function whenLocationSingletonReady(): Promise<GMXLocationSingletonApi> {
	if (window.gmxLocationSingleton) {
		return Promise.resolve(window.gmxLocationSingleton)
	}

	return new Promise(resolve => {
		window.addEventListener(LOCATION_READY_EVENT, () => resolve(window.gmxLocationSingleton), { once: true })
	})
}

/**
 * Share a zip the visitor searched for, so the next app they open already knows where they are.
 *
 * Only for a search the visitor initiated.  A search a component started on their behalf is
 * reading a zip that is already stored, and writing it back would promote a geo-IP guess to the
 * `user_input` tier the visitor never confirmed.
 *
 * @param zip - Postal code resolved from the search, if the search resolved to one.
 * @param geoDetail - Surrounding detail to store alongside it.
 */
export function persistSearchedZip(zip: string | undefined, geoDetail: AddressGeoDetail = {}): void {
	if (!zip) {
		return
	}

	window.gmxLocationSingleton?.setUserZip(zip, { country: geoDetail.country, region: geoDetail.region })
}
